export type EffectSceneCanvasPoint = readonly [number, number];

type EffectSceneCorners = readonly [
  EffectSceneCanvasPoint,
  EffectSceneCanvasPoint,
  EffectSceneCanvasPoint,
  EffectSceneCanvasPoint,
];

interface AuthoredEffectSceneOpening {
  /** top-left, top-right, bottom-right, bottom-left */
  corners: EffectSceneCorners;
  cameraDrift: EffectSceneCanvasPoint;
  cameraRoll: number;
  plateDolly: number;
}

export interface EffectSceneCanvasLayout {
  envelope: number;
  corners: EffectSceneCorners;
  plateDrawX: number;
  plateDrawY: number;
  plateDrawWidth: number;
  plateDrawHeight: number;
  plateZoom: number;
  plateRotation: number;
  portalCenterX: number;
  portalCenterY: number;
  portalZoom: number;
}

export const EFFECT_SCENE_PLATE_WIDTH = 1672;
export const EFFECT_SCENE_PLATE_HEIGHT = 941;

/**
 * Exact openings measured from the user's orange annotations on the original
 * 1672x941 plates. The fifth (Sky Elevator) uses the elevator's rear glass
 * panel because that plate was not included in the annotation set.
 */
const OPENINGS: readonly AuthoredEffectSceneOpening[] = Object.freeze([
  {
    corners: [[.4492, .2136], [.5652, .2136], [.5652, .4718], [.4492, .4718]],
    cameraDrift: [.014, -.008],
    cameraRoll: -.018,
    plateDolly: .075,
  },
  {
    corners: [[.5, .1955], [.6035, .1955], [.6035, .3751], [.5, .3751]],
    cameraDrift: [-.012, .012],
    cameraRoll: .012,
    plateDolly: .11,
  },
  {
    corners: [[.4057, .3377], [.833, .02], [.8706, .5658], [.4028, .6727]],
    cameraDrift: [.018, .006],
    cameraRoll: -.024,
    plateDolly: .065,
  },
  {
    corners: [[.3624, .2009], [.6675, .2009], [.6675, .8151], [.3624, .8151]],
    cameraDrift: [.01, -.018],
    cameraRoll: .01,
    plateDolly: .095,
  },
  {
    corners: [[.435, .49], [.575, .49], [.575, .74], [.435, .74]],
    cameraDrift: [0, -.022],
    cameraRoll: 0,
    plateDolly: .12,
  },
  {
    corners: [[.7291, .3932], [.9952, .3932], [.9952, .8204], [.7291, .8204]],
    cameraDrift: [.022, .004],
    cameraRoll: .018,
    plateDolly: .085,
  },
  {
    corners: [[.387, .2922], [.6657, .2922], [.6657, .5409], [.387, .5409]],
    cameraDrift: [-.016, -.006],
    cameraRoll: -.014,
    plateDolly: .07,
  },
  {
    corners: [[.4492, .3964], [.5622, .3964], [.5622, .5611], [.4492, .5611]],
    cameraDrift: [.008, .008],
    cameraRoll: .035,
    plateDolly: .1,
  },
  {
    corners: [[.5508, .2954], [.6274, .2954], [.6274, .4017], [.5508, .4017]],
    cameraDrift: [-.01, .015],
    cameraRoll: -.01,
    plateDolly: .09,
  },
  {
    corners: [[.4797, .3475], [.5568, .3475], [.5568, .5845], [.4797, .5845]],
    cameraDrift: [.02, -.012],
    cameraRoll: .026,
    plateDolly: .13,
  },
]);

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function smoother(value: number): number {
  const x = clamp01(value);
  return x * x * x * (x * (x * 6 - 15) + 10);
}

function rotatePoint(
  x: number,
  y: number,
  rotation: number,
): EffectSceneCanvasPoint {
  const cosine = Math.cos(rotation);
  const sine = Math.sin(rotation);
  return [
    x * cosine - y * sine,
    x * sine + y * cosine,
  ];
}

/** Scene visibility: smooth pull-back, full camera flight, exact final canvas. */
export function effectSceneEnvelope(progress: number): number {
  const enter = smoother(progress / .24);
  const finish = smoother((progress - .92) / .08);
  return enter * (1 - finish);
}

/**
 * Converts a photographed quadrilateral into output pixels after an
 * aperture-aware cover crop and the same Ken Burns camera used by WebGL.
 * Portrait/square canvases focus the crop toward the physical opening.
 */
export function effectSceneCanvasLayout(
  concept: number,
  width: number,
  height: number,
  progress: number,
): EffectSceneCanvasLayout {
  const index = Math.min(OPENINGS.length - 1, Math.max(0, Math.round(concept)));
  const opening = OPENINGS[index]!;
  const outputAspect = width / Math.max(1, height);
  const plateAspect = EFFECT_SCENE_PLATE_WIDTH / EFFECT_SCENE_PLATE_HEIGHT;
  let drawWidth = width;
  let drawHeight = height;
  let coverScaleX = 1;
  let coverScaleY = 1;
  if (outputAspect > plateAspect) {
    drawHeight = width / plateAspect;
    coverScaleY = plateAspect / outputAspect;
  } else {
    drawWidth = height * plateAspect;
    coverScaleX = outputAspect / plateAspect;
  }

  const openingFocusX = opening.corners.reduce((sum, point) => sum + point[0], 0) / 4;
  const openingFocusY = opening.corners.reduce((sum, point) => sum + point[1], 0) / 4;
  const focusX = .5 + (openingFocusX - .5) * (1 - coverScaleX);
  const focusY = .5 + (openingFocusY - .5) * (1 - coverScaleY);
  const envelope = effectSceneEnvelope(progress);
  const shotProgress = smoother(progress);
  const platePanX = (-opening.cameraDrift[0] * .5 + opening.cameraDrift[0] * shotProgress)
    * envelope;
  const platePanY = (-opening.cameraDrift[1] * .5 + opening.cameraDrift[1] * shotProgress)
    * envelope;
  const plateZoom = 1
    + opening.plateDolly
      * (.18 + .82 * shotProgress)
      * envelope;
  const plateRotation = (-opening.cameraRoll * .5 + opening.cameraRoll * shotProgress)
    * envelope;

  const baseTargetCorners = opening.corners.map((point): EffectSceneCanvasPoint => {
    const authoredX = (point[0] - focusX - platePanX) * drawWidth * plateZoom;
    const authoredY = (point[1] - focusY - platePanY) * drawHeight * plateZoom;
    const rotated = rotatePoint(authoredX, authoredY, plateRotation);
    return [width / 2 + rotated[0], height / 2 + rotated[1]];
  }) as unknown as EffectSceneCorners;
  const portalCenterX = baseTargetCorners.reduce((sum, point) => sum + point[0], 0) / 4;
  const portalCenterY = baseTargetCorners.reduce((sum, point) => sum + point[1], 0) / 4;
  const topWidth = Math.hypot(
    baseTargetCorners[1][0] - baseTargetCorners[0][0],
    baseTargetCorners[1][1] - baseTargetCorners[0][1],
  );
  const bottomWidth = Math.hypot(
    baseTargetCorners[2][0] - baseTargetCorners[3][0],
    baseTargetCorners[2][1] - baseTargetCorners[3][1],
  );
  const leftHeight = Math.hypot(
    baseTargetCorners[3][0] - baseTargetCorners[0][0],
    baseTargetCorners[3][1] - baseTargetCorners[0][1],
  );
  const rightHeight = Math.hypot(
    baseTargetCorners[2][0] - baseTargetCorners[1][0],
    baseTargetCorners[2][1] - baseTargetCorners[1][1],
  );
  const openingWidth = Math.max(1, (topWidth + bottomWidth) * .5);
  const openingHeight = Math.max(1, (leftHeight + rightHeight) * .5);
  const sourceAspect = width / Math.max(1, height);
  const openingAspect = openingWidth / openingHeight;
  const containedWidth = openingAspect > sourceAspect
    ? openingHeight * sourceAspect
    : openingWidth;
  const containedHeight = openingAspect > sourceAspect
    ? openingHeight
    : openingWidth / sourceAspect;
  const fillZoom = Math.min(
    24,
    Math.max(width / Math.max(1, containedWidth), height / Math.max(1, containedHeight)),
  );
  const pullBack = 1 - smoother(progress / .24);
  const pushIn = smoother((progress - .55) / .37);
  const portalAmount = Math.max(pullBack, pushIn);
  const portalZoom = 1 + (fillZoom - 1) * portalAmount;
  const portalTargetCorners = baseTargetCorners.map((point): EffectSceneCanvasPoint => [
    portalCenterX + (point[0] - portalCenterX) * portalZoom,
    portalCenterY + (point[1] - portalCenterY) * portalZoom,
  ]) as unknown as EffectSceneCorners;
  const fullCorners: EffectSceneCorners = [
    [0, 0],
    [width, 0],
    [width, height],
    [0, height],
  ];
  const entered = smoother(progress / .24);
  const finish = smoother((progress - .92) / .08);
  const corners = portalTargetCorners.map((point, cornerIndex): EffectSceneCanvasPoint => {
    const full = fullCorners[cornerIndex]!;
    const stagedX = full[0] + (point[0] - full[0]) * entered;
    const stagedY = full[1] + (point[1] - full[1]) * entered;
    return [
      stagedX + (full[0] - stagedX) * finish,
      stagedY + (full[1] - stagedY) * finish,
    ];
  }) as unknown as EffectSceneCorners;

  return {
    envelope,
    corners,
    plateDrawX: width / 2 - (focusX + platePanX) * drawWidth,
    plateDrawY: height / 2 - (focusY + platePanY) * drawHeight,
    plateDrawWidth: drawWidth,
    plateDrawHeight: drawHeight,
    plateZoom,
    plateRotation,
    portalCenterX,
    portalCenterY,
    portalZoom,
  };
}
